import React from 'react';
import { Card } from '../../components/ui/card';
import Button from '../../components/ui/button';
import { CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const plans = [
    {
      name: 'Free',
      price: '$0',
      description: 'Perfect for individual developers and small projects',
      features: [
        'Basic LLM Compatibility Check',
        'Limited Security Scan',
        'Monthly Report',
        'Community Support'
      ],
      buttonText: 'Start Free',
      buttonLink: '/waitlist',
      highlight: false
    },
    {
      name: 'Pro',
      price: '$49/month',
      description: 'Advanced validation for serious AI development',
      features: [
        'Comprehensive LLM Analysis',
        'Detailed Security Assessment',
        'Weekly Insights',
        'Priority Support',
        'Team Collaboration',
        'API Access'
      ],
      buttonText: 'Get Pro',
      buttonLink: '/waitlist',
      highlight: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'Tailored solutions for large-scale AI operations',
      features: [
        'Full Platform Scanning',
        'Advanced Security Evaluation',
        'Dedicated Support',
        'Custom Reporting',
        'Compliance Checks',
        'Unlimited Users'
      ],
      buttonText: 'Contact Sales',
      buttonLink: '/company/contact',
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Simple, Transparent <span className="text-primary">Pricing</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose the plan that fits your AI validation needs. Scale as you grow.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`p-6 text-center transition-all duration-300 ${
                plan.highlight
                  ? 'border-2 border-primary shadow-2xl scale-105 bg-primary/5'
                  : 'border-0 shadow-lg hover:shadow-xl'
              }`}
            >
              {plan.highlight && (
                <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 rounded-bl-lg text-xs">
                  Most Popular
                </div>
              )}
              <h2 className="text-2xl font-semibold mb-4">{plan.name}</h2>
              <div className="text-4xl font-bold mb-4">{plan.price}</div>
              <p className="text-muted-foreground mb-6">{plan.description}</p>

              <ul className="mb-8 space-y-3 text-left">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to={plan.buttonLink}>
                <Button
                  className={`w-full ${
                    plan.highlight
                      ? 'bg-primary hover:bg-primary/90'
                      : 'bg-secondary hover:bg-secondary/90'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </Link>
            </Card>
          ))}
        </div>

        <div className="text-center mt-16 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold mb-4">Need a Custom Solution?</h3>
          <p className="text-muted-foreground mb-6">
            Our Enterprise plan offers tailored solutions for complex AI validation requirements.
            Contact our sales team to discuss your specific needs.
          </p>
          <Link to="/company/contact">
            <Button variant="outline">
              Talk to Sales
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
